<script>
import { fandomBasicMixin, fandomExtendedMixin } from "src/modules/fandom_mixins_module.js";
import { get } from "lodash";
import StripeHeader from "stripe-header";

export default {
  mixins: [fandomBasicMixin, fandomExtendedMixin],
  components: {
    StripeHeader
  },
  props: {
    alternativeAppendContents: Function,
    childrenLayoutOverride: String,
    optContentName: String,
    showTitle: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hideInitialTile: true,
      getContentsParams: {
        limit: (this.content.limit || 8),
        content_filtering: [],
        include_extra_field_content_filtering: true,
        include_children: true
      },
      paddingYDefault: 4
    }
  },
  mounted() {
    const params = new URLSearchParams(window.location.search);
    this.hideInitialTile = !!params.get('filters');

    Fandom.onEvent("grid-filter-apply", this.handleGridFilterApply);

    Vue.nextTick(() => {
      Fandom.emit('grid-fixed:filters', this.content.children_count);
    })
  },
  methods: {
    getPrimaryButtonClass: Fandom.getPrimaryButtonClass,
    handleGridFilterApply(data) {
      if (data.handlerContents.indexOf(this.content.name) > -1) {
        this.getContentsParams.content_filtering = data.contentFiltering;
        Fandom.getContents(this.content.name, this.getContentsParams, () => {
          const filtersActive = data.contentFiltering.reduce((response,filters) => (response || filters.length > 0), false);
          const urlParams = new URLSearchParams(window.location.search);
          const queryString = Array.from(urlParams.entries())
            .filter(entry => entry[0] !== 'filters')
            .map(entry => `${entry[0]}=${entry[1]}`)
            .join('&');

          if (filtersActive) {
            const activeFiltersSerialized = encodeURIComponent(JSON.stringify(data.contentFiltering));
            history.pushState({}, '', `${window.location.origin}${window.location.pathname}?filters=${activeFiltersSerialized}&${queryString}`);
          } else {
            history.pushState({}, '', `${window.location.origin}${window.location.pathname}?${queryString}`);
          }

          this.hideInitialTile = false;
          Fandom.emit('grid-fixed:filters', this.content.children_count);
        });
      } else {
        this.hideInitialTile = false;
      }
    },
    appendContents() {
      const count = get(this.content, "contents_count", null);
      if (Fandom.exists(this.alternativeAppendContents)) {
        this.alternativeAppendContents(true);
      } else {
        Fandom.appendContents(this.content.name, () => {
          Fandom.emit('grid-fixed:filters', this.content.children_count);
        }, count, this.getContentsParams);
      }
    },
    childLayout(child) {
      return Fandom.exists(this.childrenLayoutOverride) ? this.childrenLayoutOverride : this.getContent(child).layout;
    }
  },
  computed: {
    containerClasses() {
      return [
        this.themeClass
      ];
    },
    themeClass: function() {
      if (this.content.theme) {
        return `${this.content.theme}-theme`;
      }
    },
    mainStyles() {   
      if (!this.content.theme) {
        return {
          "background-color": get(this.content, "background_color", "var(--white)"),
          "color": get(this.content, "text_color", "var(--dark)")
        }
      }
    },
    rowColsClasses() {
      const configs = {
        1: ["row-cols-1 row-cols-sm-2 row-cols-lg-3"],
        2: ["row-cols-1 row-cols-md-3 row-cols-xl-6"],
        3: ["row-cols-1 row-cols-sm-2 row-cols-lg-4"],
        4: ["row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-4"],
        5: ["row-cols-1 row-cols-sm-2"]
      };
      const set = this.content.set ? parseInt(this.content.set) : 2;
      const classes = configs[set];
      classes.push(`justify-content-${get(this.content, "align_tiles", "center")}`);
      return classes;
    },
    aspectRatioClass() {
      return get(this.content, "aspect_ratio_class", "ar-2by3");
    },
    childrenCount() {
      return this.hideInitialTile ? -1 : this.content.children_count;
    }
  },
};

</script>

<template>
  <div 
    v-easyadmin="optContentName ? optContentName : easyadminId" 
    v-if="isContentVisibleToUser()" 
    :class="[contentType, themeClass, paddingY]" 
    :style="mainStyles" 
    fixed-grid ref="grid" 
    class="h-100"
    :id="contentId"
  >
    <span v-if="content.stripe_id" class="anchor" :id="content.stripe_id"></span>
    <slot name="grid-header" :resultsCount="childrenCount">
      <stripe-header :content="content" v-if="showTitle"></stripe-header>
    </slot>
    <div :class="containerClasses" class="container-fluid my-4 px-3">
      <div class="d-flex w-100 h-100 align-items-center justify-content-center" v-if="hideInitialTile || !content.children">
        <i class="fal fa-circle-notch fa-spin fa-2x"></i>
      </div>
      <div v-else-if="content.children.length == 0" class="w-100 d-flex justify-content-center">
        <p class="text-uppercase text-muted">{{ft("globals.no_content")}}</p>
      </div>
      <div v-else class="row m-n2" :class="rowColsClasses">
        <template v-if="!hideInitialTile">
          <div class="col p-custom" v-for="(child, index) in content.children" :key="`item-${child}`">
            <component
              :is="childLayout(child)"
              :class="aspectRatioClass"
              :content="getContent(child)"
              :index="content.children_count - index"
              class="d-block w-100 high position-relative"
              :iconsBottom="aspectRatioClass === 'ar-2by3'"
              tile
            ></component>
          </div>
        </template>
      </div>
    </div>
    <slot name="grid-footer">
      <div v-if="content.has_more_children && !content.url" class="container my-4">
        <div class="row">
          <div class="col-sm-12 text-center">
            <button class="btn shadow-none" :class="getSecondaryButtonClass(content.button_class, content.theme)" @click="appendContents()">{{ft("globals.load_more")}}</button>
          </div>
        </div>
      </div>
      <div class="w-100 d-flex justify-content-center" v-if="content.url">
        <a :href="content.url.url" :target="getTarget(content.url)" class="btn" :class="getSecondaryButtonClass(content.button_class, content.theme)">
          {{content.url.title}}
        </a>
      </div>
    </slot>
  </div>
</template>

<style lang="scss">
[fixed-grid] {
  .high {
    .icon {
      height: 30% !important;
    }
  }

  .p-custom {
    padding: $grid-gap / 2;
  }
}
</style>
